/* eslint-disable prefer-const */
import { PayloadAction } from "@reduxjs/toolkit";
import {
  closeAppBackdrop,
  notify,
  showAppBackdrop,
} from "features/app/store/slice";
import { call, put, select, takeEvery } from "redux-saga/effects";
import { IApiResponse } from "types/api";
import { IPrivateChatData } from "types/models/private-chat";
import { formatPath, getQueryString } from "utils";
import { apiGet, apiPost } from "utils/api";
import PrivateChatPaths from "../models/paths";
import {
  addLLMModel,
  addLLMModelSuccess,
  editLLMModel,
  editLLMModelSuccess,
  fetchAnswerWebosAPI,
  fetchApiTree,
  fetchApiTreeSuccess,
  fetchDataAnswer,
  fetchDataAnswerFailure,
  fetchDataAnswerSuccess,
  fetchDataLLMModel,
  fetchDataLLMModelSuccess,
  fetchMessageWelcome,
  fetchMessageWelcomeFailure,
  fetchMessageWelcomeSuccess,
  fetchPermissionTree,
  fetchPermissionTreeSuccess,
  getMaxToken,
  getSessionHistory,
} from "./slice";

export const apiFetchMessageWelcome = (query: Partial<any>) => {
  const queryString = getQueryString({ ...query });
  return apiGet<any>(
    formatPath(PrivateChatPaths.get_message_welcome, queryString)
  );
};

function* handleFetchMessageWelcome(action: PayloadAction<any>) {
  yield put(showAppBackdrop());
  try {
    const response: IApiResponse<any> = yield call(
      apiFetchMessageWelcome,
      action.payload
    );
    if (response.data) {
      if (
        response.data?.statusCode === 500 ||
        response.data?.statusCode === 502
      ) {
        yield put(
          fetchMessageWelcomeSuccess({
            ...response.data,
          })
        );
      } else {
        yield put(fetchMessageWelcomeSuccess(response.data));
      }
      yield put(closeAppBackdrop());
    } else {
      yield put(fetchMessageWelcomeFailure());
      yield put(closeAppBackdrop());
    }
  } catch (e) {
    yield put(closeAppBackdrop());
  }
}

export const apiFetchDataLLMModel = () => {
  return apiGet<any>(formatPath(PrivateChatPaths.llm_model));
};

function* handleFetchDataLLMModel() {
  yield put(showAppBackdrop());
  try {
    const response: IApiResponse<any> = yield call(apiFetchDataLLMModel);
    if (response.data) {
      yield put(fetchDataLLMModelSuccess(response.data));
      yield put(closeAppBackdrop());
    } else {
      if (response.detail !== "") {
        yield put(
          notify(`${response.detail}`, {
            options: { variant: "error" },
          })
        );
      }
      yield put(closeAppBackdrop());
    }
  } catch (e) {
    yield put(closeAppBackdrop());
  }
}

export const apiFetchAnswer = (query: Partial<any>) => {
  return apiPost<IPrivateChatData>(
    formatPath(PrivateChatPaths.get_private_chat),
    { ...query }
  );
};

function* handleFetchDataAnswer(
  action: PayloadAction<{
    check_log_names: Array<string>;
    answer: string;
    checked: boolean;
    check_documents: Array<string>;
    chat_page: string;
  }>
) {

  try {
    const sessionHistory: Array<[]> = yield select(getSessionHistory);
    const maxToken: number = yield select(getMaxToken);
    const response: IApiResponse<any> = yield call(apiFetchAnswer, {
      question: action.payload.answer,
      checked: action.payload.checked,
      chat_page: action.payload.chat_page,
      check_documents: action.payload.check_documents,
      check_log_names: action.payload.check_log_names,
      chat_history: sessionHistory,
      max_token: maxToken
    });
    if (response.data) {
      if (
        response.data?.statusCode === 500 ||
        response.data?.statusCode === 502
      ) {
        yield put(
          fetchDataAnswerSuccess({
            ...response.data,
          })
        );
      } else {
        yield put(fetchDataAnswerSuccess(response.data));
      }
    } else {
      yield put(fetchDataAnswerFailure());
    }
  } catch (e) {
    //
  }
}

export const apiFetchAnswerWebosAPI = (query: Partial<any>) => {
  return apiPost<IPrivateChatData>(
    formatPath(PrivateChatPaths.get_webos_chat),
    { ...query }
  );
};

function* handleFetchAnswerWebosAPI(
  action: PayloadAction<{
    answer: string;
    checked: boolean;
    check_documents: Array<string>;
    chat_page: string;
    type: string;
  }>
) {
  try {
    const sessionHistory: Array<[]> = yield select(getSessionHistory);
    const maxToken: number = yield select(getMaxToken);
    const response: IApiResponse<any> = yield call(apiFetchAnswerWebosAPI, {
      question: action.payload.answer,
      checked: action.payload.checked,
      chat_page: action.payload.chat_page,
      check_documents: action.payload.check_documents,
      type: action.payload.type,
      chat_history: sessionHistory,
      max_token: maxToken
    });
    if (response.data) {
      if (
        response.data?.statusCode === 500 ||
        response.data?.statusCode === 502
      ) {
        yield put(
          fetchDataAnswerSuccess({
            ...response.data,
          })
        );
      } else {
        yield put(fetchDataAnswerSuccess(response.data));
      }
    } else {
      yield put(fetchDataAnswerFailure());
    }
  } catch (e) {
    //
  }
}

export const apiEditLLMModel = (action: any) => {
  return apiPost<any>(formatPath(PrivateChatPaths.update_llm_model), action);
};

function* handleEditLLMModel(action: PayloadAction<any>) {
  try {
    yield put(showAppBackdrop());
    const response: IApiResponse<any> = yield call(
      apiEditLLMModel,
      action.payload
    );
    if (response.data) {
      yield put(editLLMModelSuccess(response.data.data));
      yield put(
        notify(`${response.data.detail}`, {
          options: { variant: "success" },
        })
      );
    } else {
      if (response.detail !== "") {
        yield put(
          notify(`${response.detail}`, {
            options: { variant: "error" },
          })
        );
      }
    }
    yield put(closeAppBackdrop());
  } catch (e) {
    yield put(closeAppBackdrop());
    //
  }
}

export const apiAddLLMModel = (action: any) => {
  return apiPost<any>(formatPath(PrivateChatPaths.add_llm_model), action);
};

function* handleAddLLMModel(action: PayloadAction<any>) {
  try {
    yield put(showAppBackdrop());
    const response: IApiResponse<any> = yield call(
      apiAddLLMModel,
      action.payload
    );
    if (response.data) {
      yield put(addLLMModelSuccess(response.data.data));
      yield put(
        notify(`${response.data.detail}`, {
          options: { variant: "success" },
        })
      );
    } else {
      if (response.detail !== "") {
        yield put(
          notify(`${response.detail}`, {
            options: { variant: "error" },
          })
        );
      }
    }
    yield put(closeAppBackdrop());
  } catch (e) {
    yield put(closeAppBackdrop());
    //
  }
}

export const apiFetchPermissionTree = (query: Partial<any>) => {
  const queryString = getQueryString({ ...query });
  return apiGet<any>(
    formatPath(PrivateChatPaths.get_permission_tree, queryString)
  );
};

function* handleFetchPermissionTree(action: PayloadAction<any>) {
  yield put(showAppBackdrop());
  try {
    const response: IApiResponse<any> = yield call(apiFetchPermissionTree, action.payload);
    if (response.data) {
      yield put(fetchPermissionTreeSuccess(response.data.data));
      yield put(closeAppBackdrop());
    } else {
      if (response.detail !== "") {
        yield put(
          notify(`${response.detail}`, {
            options: { variant: "error" },
          })
        );
      }
      yield put(closeAppBackdrop());
    }
  } catch (e) {
    yield put(closeAppBackdrop());
  }
}

export const apiFetchApiTree = (query: Partial<any>) => {
  const queryString = getQueryString({ ...query });
  return apiGet<any>(formatPath(PrivateChatPaths.get_api_tree, queryString));
};

function* handleFetchApiTree(action: PayloadAction<any>) {
  yield put(showAppBackdrop());
  try {
    const response: IApiResponse<any> = yield call(
      apiFetchApiTree,
      action.payload
    );
    if (response.data) {
      yield put(fetchApiTreeSuccess(response.data.data));
      yield put(closeAppBackdrop());
    } else {
      if (response.detail !== "") {
        yield put(
          notify(`${response.detail}`, {
            options: { variant: "error" },
          })
        );
      }
      yield put(closeAppBackdrop());
    }
  } catch (e) {
    yield put(closeAppBackdrop());
  }
}

export default function* PrivateChatSaga() {
  yield takeEvery(fetchDataAnswer.type, handleFetchDataAnswer);
  yield takeEvery(fetchAnswerWebosAPI.type, handleFetchAnswerWebosAPI);
  yield takeEvery(fetchDataLLMModel.type, handleFetchDataLLMModel);
  yield takeEvery(fetchPermissionTree.type, handleFetchPermissionTree);
  yield takeEvery(fetchApiTree.type, handleFetchApiTree);
  yield takeEvery(fetchMessageWelcome.type, handleFetchMessageWelcome);
  yield takeEvery(editLLMModel.type, handleEditLLMModel);
  yield takeEvery(addLLMModel.type, handleAddLLMModel);
}
